import React, { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Linkedin, Loader2, Mail, MessageCircle, RefreshCw, Send, Share2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

/**
 * Share a listing, or send it to someone who is not on Trados yet.
 *
 * Every channel is logged server-side (/share) so the super admin can see which
 * listings actually travel. The email path reuses the same signed single-use
 * captcha as the public comment form — it sends mail to an address the visitor
 * chooses, so it needs the check at least as much.
 */
const DirectoryShareBox = ({ slug, companyName }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ to_email: '', to_name: '', from_name: '', from_email: '', message: '', answer: '', website: '' });
  const [challenge, setChallenge] = useState(null);
  const [loadingCaptcha, setLoadingCaptcha] = useState(false);
  const [sending, setSending] = useState(false);

  const url = `${window.location.origin}/directory/${slug}`;
  const shareText = `${companyName} on Trados — the verified network for global commodity trade`;

  const loadCaptcha = useCallback(async () => {
    setLoadingCaptcha(true);
    try {
      const res = await api.get('/public/directory/captcha');
      setChallenge(res.data);
    } catch {
      setChallenge(null);
    } finally {
      setLoadingCaptcha(false);
    }
  }, []);

  // Only fetch a challenge once the form is actually opened — a token expires
  // in ten minutes and most visitors never open it.
  useEffect(() => { if (open && !challenge) loadCaptcha(); }, [open, challenge, loadCaptcha]);

  // Logging must never block or break sharing, so failures stay silent.
  const log = (channel) => { api.post(`/public/directory/${slug}/share`, { channel }).catch(() => {}); };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Link copied.');
      log('copy');
    } catch {
      toast.error('Could not copy the link.');
    }
  };

  const social = (channel) => {
    const targets = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${url}`)}`,
    };
    window.open(targets[channel], '_blank', 'noopener,noreferrer');
    log(channel);
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const emailOk = (v) => /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test((v || '').trim());
  const ready = emailOk(form.to_email) && emailOk(form.from_email) && form.from_name.trim().length >= 2
    && form.answer.trim() !== '' && !!challenge;

  const send = async () => {
    if (!ready) return;
    setSending(true);
    try {
      const res = await api.post(`/public/directory/${slug}/share/email`, {
        to_email: form.to_email.trim(),
        to_name: form.to_name.trim(),
        from_name: form.from_name.trim(),
        from_email: form.from_email.trim(),
        message: form.message.trim(),
        captcha_token: challenge.token,
        captcha_answer: form.answer.trim(),
        website: form.website,
      });
      toast.success(res.data.message || 'Sent.');
      setForm({ to_email: '', to_name: '', from_name: '', from_email: '', message: '', answer: '', website: '' });
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send that email.');
    } finally {
      // The token is spent either way, so always take a fresh one.
      setForm((f) => ({ ...f, answer: '' }));
      loadCaptcha();
      setSending(false);
    }
  };

  const input = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const chip = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border bg-white border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors';

  return (
    <div data-testid="directory-share">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={copy} className={chip} data-testid="share-copy">
          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copied' : 'Copy link'}
        </button>
        <button type="button" onClick={() => social('linkedin')} className={chip} data-testid="share-linkedin" aria-label="Share on LinkedIn">
          <Linkedin className="w-4 h-4" /> LinkedIn
        </button>
        <button type="button" onClick={() => social('x')} className={chip} data-testid="share-x" aria-label="Share on X">
          <X className="w-4 h-4" /> X
        </button>
        <button type="button" onClick={() => social('whatsapp')} className={chip} data-testid="share-whatsapp" aria-label="Share on WhatsApp">
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="share-email-toggle"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-white"
          style={{ backgroundColor: BRAND_BLUE }}
        >
          <Send className="w-4 h-4" /> Send to a friend
        </button>
      </div>

      {open && (
        <div className="mt-4 p-4 rounded-xl border border-gray-200 bg-gray-50/60 space-y-3" data-testid="share-email-form">
          <p className="text-xs text-gray-500 flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" />
            We email them this listing and invite them to join Trados. No mailing list, no follow-ups.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <input value={form.to_name} onChange={set('to_name')} maxLength={80}
                   placeholder="Their name (optional)" data-testid="share-to-name" className={input} />
            <input value={form.to_email} onChange={set('to_email')} type="email" maxLength={120}
                   placeholder="Their email *" data-testid="share-to-email" className={input} />
            <input value={form.from_name} onChange={set('from_name')} maxLength={80}
                   placeholder="Your name *" data-testid="share-from-name" className={input} />
            <input value={form.from_email} onChange={set('from_email')} type="email" maxLength={120}
                   placeholder="Your email *" data-testid="share-from-email" className={input} />
          </div>

          <textarea value={form.message} onChange={set('message')} rows={2} maxLength={500}
                    placeholder="Add a short note (optional)" data-testid="share-message"
                    className={`${input} resize-none`} />

          {/* Honeypot */}
          <input value={form.website} onChange={set('website')} tabIndex={-1} autoComplete="off"
                 aria-hidden="true" className="absolute w-px h-px -left-[9999px] opacity-0" />

          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-gray-600" data-testid="share-captcha-question">
              {loadingCaptcha ? 'Loading…' : (challenge?.question || 'Captcha unavailable')}
            </span>
            <button type="button" onClick={loadCaptcha} aria-label="Get a new captcha question"
                    className="text-gray-400 hover:text-gray-600">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingCaptcha ? 'animate-spin' : ''}`} />
            </button>
            <input value={form.answer} onChange={set('answer')} inputMode="numeric" maxLength={4}
                   placeholder="Answer" data-testid="share-captcha" className={`${input} w-24`} />
            <button
              type="button"
              onClick={send}
              disabled={!ready || sending}
              data-testid="share-send"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectoryShareBox;
